import { keyFactory, uncachedSelector } from 'src/utils/recoil'
import * as mapAtoms from 'src/atoms/main-map'
import { BorderData, Borders } from 'src/components/map-layers/borders-layers/types'
import { demoClient, vendorsClient } from 'src/clients'
import { DefaultValue, atom, noWait, selectorFamily } from 'recoil'
import { Feature, PropertiesBorderResponse, ShapesRequest } from 'src/common-services'
import { MultiPolygon, Polygon } from '@turf/helpers'
import orderBy from 'lodash/orderBy'
import turf from 'src/utils/turf'
import { resolution5Hexes, roundedZoom } from 'src/atoms/parcel-boundaries'
import { Bounds } from 'src/types/map'
import { getLastLocation, lastLocationKey } from 'src/utils/map'

const key = keyFactory('borders-layer')

export const borderType = atom<Borders>({
  key: key('border-type'),
  default: Borders.NONE,
})

// we have a defined a different polygon for the borders layer when a border is selected
// we can't use the custom area polygon because some border polygons are multi-polygons and mobility and tta reports don't work with multi-polygons
export const polygon = atom<Polygon | GeoJSON.MultiPolygon | undefined>({
  key: key('borders-polygon'),
  default: undefined,
})

const mapPropertiesBorderResponseToBorderData = (borderResponse: PropertiesBorderResponse): BorderData[] => {
  return borderResponse.features.map((item) => {
    return {
      name: item.properties.name,
      polygon: item.geometry,
    }
  })
}

export const PAGE_SIZE = 100
export const MAX_PAGES = 2

async function fetchMany(
  fn: (req: ShapesRequest) => Promise<PropertiesBorderResponse>,
  polygon: MultiPolygon
): Promise<BorderData[]> {
  const result = await fn({
    polygon,
  })
  if (!result?.features || result.features?.length === 0) {
    return []
  }
  if (result.features.length === PAGE_SIZE) {
    for (let i = 1; i < MAX_PAGES; i++) {
      const lastFeature = result.features[result.features.length - 1]
      const nextPage = await fn({ polygon, startingId: lastFeature.properties.name })
      result.features = result.features.concat(nextPage.features)
      if (nextPage.features.length < PAGE_SIZE) {
        break
      }
    }
  }
  const response = mapPropertiesBorderResponseToBorderData(result)
  // sort so the littler ones are on top and rendered first for better tooltips
  // TODO(bmc): this only takes a few milliseconds on my computer, but might be slow on low power devices?
  const sorted = orderBy(response, (item) => turf.area(item.polygon), ['desc'])
  return sorted
}

const getCities = vendorsClient.properties.getCities.bind(vendorsClient.properties)
const citiesBorders = uncachedSelector<BorderData[]>({
  key: key('cities-borders'),
  get: async ({ get }) => {
    const { polygon } = get(mapAtoms.mapInfoPolygon)
    return fetchMany(getCities, polygon)
  },
})

const zipCodesShapeForH3 = selectorFamily({
  key: key('zipCodesShapeForH3'),
  get: (h3R5Id: string) => async () => {
    return (await vendorsClient.properties.getZipCodesByH3R5Id(h3R5Id)).features
  },
})

const zipCodesBorders = uncachedSelector<BorderData[]>({
  key: key('zip-codes-borders'),
  get: async ({ get }) => {
    const h3R5s = get(resolution5Hexes)
    const zoom = get(roundedZoom)
    if (zoom < 10) {
      return []
    }
    let res: any[] = []
    for (const h3R5 of h3R5s) {
      const result = get(noWait(zipCodesShapeForH3(h3R5)))
      if (result.state === 'hasValue') {
        res = res.concat(
          (result.contents ?? []).filter((x) => res.find((y) => y.properties.name === x.properties.name) === undefined)
        )
      }
    }
    const response = mapPropertiesBorderResponseToBorderData({ type: 'FeatureCollection', features: res })
    return orderBy(response, (item) => turf.area(item.polygon), ['desc'])
  },
})

const getNeighborhoods = vendorsClient.properties.getNeighborhoods.bind(vendorsClient.properties)
const neighborhoodsBorders = uncachedSelector<BorderData[]>({
  key: key('neighborhoods-borders'),
  get: async ({ get }) => {
    const { polygon } = get(mapAtoms.mapInfoPolygon)
    return fetchMany(getNeighborhoods, polygon)
  },
})

const censusBlockGroups = uncachedSelector<BorderData[]>({
  key: key('census-block-groups-borders'),
  get: async ({ get }) => {
    const { polygon } = get(mapAtoms.mapInfoPolygon)
    const blocks = await demoClient.getBlockGroupPolygons({ polygon })
    return blocks.map((block) => {
      return {
        name: block.id,
        polygon: JSON.parse(block.polygon),
      }
    })
  },
})

// const parcelBoundariesBorders = uncachedSelector<BorderData[]>({
//   key: key('parcel-boundaries-borders'),
//   get: async ({ get }) => {
//     const polygon = get(mapAtoms.mapInfoPolygon)
//     return mapPropertiesBorderResponseToBorderData(await vendorsClient.properties.getParcelBoundaries(polygon))
//   },
// })

// function assertNever(x: never): never {
//   throw new Error('Unexpected object: ' + x)
// }

export const bounds = atom<Bounds>({
  key: key('bounds'),
  default: getLastLocation(),
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newVal: Bounds | DefaultValue) => {
        if (newVal instanceof DefaultValue) {
          return
        }
        localStorage.setItem(lastLocationKey, JSON.stringify(newVal))
      })
    },
  ],
})

export const borders = uncachedSelector<BorderData[]>({
  key: key('borders'),
  get: ({ get }) => {
    const selectedBorderType = get(borderType)
    switch (selectedBorderType) {
      case Borders.ZIP_CODES:
        return zipCodesBorders
      case Borders.CITIES:
        return citiesBorders
      case Borders.NEIGHBORHOODS:
        return neighborhoodsBorders
      case Borders.CENSUS_BLOCK_GROUPS:
        return censusBlockGroups
      case Borders.NONE:
        return []
      default:
        return []
    }
  },
})

export const features = uncachedSelector<Feature[]>({
  key: key('features'),
  get: ({ get }) => {
    const brds = get(borders)
    return brds.map((item) => {
      return {
        type: 'Feature',
        geometry: item.polygon,
        properties: {
          name: item.name,
          // TODO(bmc): get the id of the place from cherre
          id: Math.random(),
        },
      }
    })
  },
})
